// Shared helpers for AdditionalComponent conditions (join-level and component-level).
// Shape (backend contract): legacy keys + `rules`; unknown top-level keys are preserved.

export type FabricGroupRule = {
  type: 'fabricGroup'
  op: 'in' | 'notIn'
  codes: string[]
  source?: 'erp' | 'manual'
}

export type ConditionsValue = {
  onlyWithComponents?: string[]
  onlyWithComponentsGroup?: string
  rules?: Array<FabricGroupRule | Record<string, any>>
  [key: string]: any
} | null

export const isFabricRule = (r: any): r is FabricGroupRule =>
  !!r && r.type === 'fabricGroup' && (r.op === 'in' || r.op === 'notIn')

/** Drop empty parts; an entirely empty object becomes null (backend stores NULL). */
export function normalizeConditions(value: any): ConditionsValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const out: Record<string, any> = { ...value }
  delete out.__typename

  const comps = Array.isArray(out.onlyWithComponents)
    ? out.onlyWithComponents.filter((c: any) => c !== '' && c != null)
    : []
  if (comps.length) out.onlyWithComponents = comps
  else {
    delete out.onlyWithComponents
    delete out.onlyWithComponentsGroup
  }
  if (!out.onlyWithComponentsGroup) delete out.onlyWithComponentsGroup

  if (Array.isArray(out.rules)) {
    const rules = out.rules.filter(
      (r: any) => !(isFabricRule(r) && r.codes.length === 0)
    )
    if (rules.length) out.rules = rules
    else delete out.rules
  } else {
    delete out.rules
  }

  return Object.keys(out).length ? (out as ConditionsValue) : null
}

/** Short human summary, e.g. "Only with threads-type: 10 · Fabric: only 2 groups". */
export function summarizeConditions(value: any): string[] {
  const c = normalizeConditions(value)
  if (!c) return []
  const parts: string[] = []
  if (c.onlyWithComponents?.length) {
    parts.push(
      `Only with ${c.onlyWithComponentsGroup || 'components'}: ${c.onlyWithComponents.join(', ')}`
    )
  }
  for (const r of c.rules ?? []) {
    if (isFabricRule(r)) {
      const n = r.codes.length
      parts.push(
        `Fabric: ${r.op === 'in' ? 'only' : 'not'} ${n} group${n === 1 ? '' : 's'}`
      )
    } else {
      parts.push(`Rule: ${r?.type ?? 'unknown'}`)
    }
  }
  const known = new Set(['onlyWithComponents', 'onlyWithComponentsGroup', 'rules'])
  if (Object.keys(c).some(k => !known.has(k))) parts.push('Other conditions')
  return parts
}

/** Pull a readable message out of an Apollo error (BAD_USER_INPUT carries extensions.path). */
export function conditionsErrorMessage(error: any): string | null {
  const gqlErrors: any[] = error?.graphQLErrors ?? []
  const bad = gqlErrors.find(e => e?.extensions?.code === 'BAD_USER_INPUT')
  if (!bad) return null
  const path = bad.extensions?.path
  const where = Array.isArray(path) ? path.join('.') : path
  return where ? `${bad.message} (${where})` : bad.message
}
